import React, { type Dispatch, type SetStateAction, useState } from 'react';
import {
  Badge,
  Box,
  Fab,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { useIdleNudge } from './hooks/useIdleNudge';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { Chatbot } from './Chatbot';
import useAppTheme from 'hooks/useAppTheme';
import { ChatbotProvider, useChatbotContext } from './context/ChatbotContext';
import {
  CancelOutlined,
  FullscreenExitRounded,
  FullscreenRounded,
  SmartToyRounded,
} from '@mui/icons-material';
import { ReactComponent as DarkModeLogo } from 'assets/logos/symbot-icon-blue.svg';
import {
  CLIENT_THEME,
  clientThemePalette,
  getClientLogoSmall,
} from 'utils/clientTheme';
import { useTranslation } from 'react-i18next';

export const ChatbotFloatingContainer = ({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const { isExpanded, setIsExpanded } = useChatbotContext();
  const colorMode = enableDarkTheme ? 'dark' : 'light';
  const themeColors = clientThemePalette[CLIENT_THEME][colorMode];
  const clientLogo = getClientLogoSmall();

  // Derive border / glow accent from theme
  const accentColor = themeColors.primary;
  const accentAlpha = `${accentColor}99`;
  const accentFaint = `${accentColor}22`;

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'fixed',
        bottom: 12,
        right: 12,
        width: isExpanded ? '60vw' : '25vw',
        height: isExpanded ? 'calc(100vh - 75px)' : '60vh',
        borderRadius: 4,
        overflow: 'hidden',
        background: enableDarkTheme ? themeColors.navbarBg : '#f8fafc',
        border: `1.5px solid ${accentAlpha}`,
        boxShadow: `0px 16px 40px rgba(0,0,0,0.35), 0px 0px 0px 1px ${accentFaint}`,
        transition: `
          width 0.4s cubic-bezier(0.4, 0, 0.2, 1),
          height 0.4s cubic-bezier(0.4, 0, 0.2, 1),
          border-radius 0.3s ease,
          transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
          opacity 0.3s ease
        `,
        transformOrigin: 'bottom right',
        transform: isOpen ? 'scale(1)' : 'scale(0.85)',
        opacity: isOpen ? 1 : 0,
        pointerEvents: isOpen ? 'all' : 'none',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            background: enableDarkTheme
              ? `linear-gradient(90deg, ${accentColor}33 0%, ${themeColors.navbarBg} 100%)`
              : `linear-gradient(90deg, ${accentColor}18 0%, #f1f5f9 100%)`,
            borderBottom: `1px solid ${accentColor}33`,
          }}
        >
          {/* Logo + title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '8px',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `${accentColor}22`,
                border: `1px solid ${accentColor}44`,
                flexShrink: 0,
              }}
            >
              {clientLogo.type === 'img' ? (
                <img
                  src={clientLogo.src}
                  alt={clientLogo.alt}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <SmartToyRounded sx={{ fontSize: 18, color: accentColor }} />
              )}
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  color: enableDarkTheme ? '#fff' : '#1e293b',
                  letterSpacing: '0.02em',
                  lineHeight: 1,
                }}
              >
                SYMX.BOT
                <Box
                  component="span"
                  sx={{
                    ml: 0.5,
                    fontSize: '0.6rem',
                    fontWeight: 500,
                    opacity: 0.55,
                    verticalAlign: 'super',
                  }}
                >
                  alpha
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  color: accentColor,
                  opacity: 0.85,
                  lineHeight: 1,
                  mt: 0.3,
                }}
              >
                {t('ai_assistant')}
              </Typography>
            </Box>
          </Box>

          {/* Controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={isExpanded ? 'Minimize' : 'Maximize'} arrow>
              <IconButton
                size="small"
                onClick={() => {
                  setIsExpanded(!isExpanded);
                }}
                sx={{
                  color: enableDarkTheme ? 'rgba(255,255,255,0.6)' : '#64748b',
                  '&:hover': {
                    color: accentColor,
                    background: `${accentColor}18`,
                  },
                }}
              >
                {isExpanded ? (
                  <FullscreenExitRounded fontSize="small" />
                ) : (
                  <FullscreenRounded fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title={t('close')} arrow>
              <IconButton
                size="small"
                onClick={() => {
                  setIsOpen(false);
                }}
                sx={{
                  color: enableDarkTheme ? 'rgba(255,255,255,0.5)' : '#94a3b8',
                  '&:hover': {
                    color: '#ef4444',
                    background: 'rgba(239,68,68,0.1)',
                  },
                }}
              >
                <CancelOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Chat body */}
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            borderRadius: '0 0 14px 14px',
          }}
        >
          <Chatbot />
        </Box>
      </Box>
    </Paper>
  );
};

export const ChatBotFloatingButton: React.FC = () => {
  const { t } = useTranslation();
  const { enableDarkTheme: isDark } = useAppTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [showBadge, setShowBadge] = useState(false);
  const [showNudgePopup, setShowNudgePopup] = useState(false);

  useIdleNudge(() => {
    setShowBadge(true);
    setShowNudgePopup(true);
    // Subtle pop sound via Web Audio API — no external file needed
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(220, ctx.currentTime + 0.08);
      gain.gain.setValueAtTime(0.18, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.12);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.12);
      osc.onended = async () => {
        await ctx.close();
      };
    } catch {
      // Web Audio not supported — fail silently
    }
  });

  // Auto-dismiss popup after 8 seconds
  React.useEffect(() => {
    if (!showNudgePopup) return;
    const t = setTimeout(() => {
      setShowNudgePopup(false);
    }, 8000);
    return () => {
      clearTimeout(t);
    };
  }, [showNudgePopup]);

  const handleOpenChatbot = () => {
    setIsOpen(true);
    setShowBadge(false);
    setShowNudgePopup(false);
  };

  const colorMode = 'dark'; // FAB always sits on dark background of app
  const themeColors = clientThemePalette[CLIENT_THEME][colorMode];
  const accentColor = themeColors.primary;
  const clientLogo = getClientLogoSmall();

  return (
    <CustomMuiThemeProvider>
      <ChatbotProvider>
        <Box sx={{ position: 'fixed', zIndex: 1000, bottom: 0, right: 0 }}>
          {/* Floating button */}
          <Box
            sx={{
              position: 'fixed',
              bottom: 14,
              right: 14,
              display: isOpen ? 'none' : 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 1,
              '@keyframes float': {
                '0%, 100%': { transform: 'translateY(0px)' },
                '50%': { transform: 'translateY(-6px)' },
              },
              '@keyframes shimmer': {
                '0%': { backgroundPosition: '200% center' },
                '100%': { backgroundPosition: '-200% center' },
              },
              animation: 'float 3s ease-in-out infinite',
              borderRadius: '18px 18px 4px 18px',
              '&:hover': {
                animation: 'none',
                transform: 'scale(1.07)',
              },
              transition: 'transform 0.2s ease',
            }}
          >
            {/* Speech-bubble nudge popup */}
            {showNudgePopup && (
              <Box
                onClick={handleOpenChatbot}
                sx={{
                  position: 'relative',
                  maxWidth: 220,
                  px: 1.5,
                  py: 1,
                  borderRadius: '12px 12px 4px 12px',
                  background: '#1e293b',
                  border: `1.5px solid ${accentColor}99`,
                  boxShadow: `0 4px 20px rgba(0,0,0,0.4), 0 0 0 1px ${accentColor}22`,
                  cursor: 'pointer',
                  animation:
                    'nudge-in 0.3s cubic-bezier(0.34,1.56,0.64,1) forwards',
                  '@keyframes nudge-in': {
                    '0%': {
                      opacity: 0,
                      transform: 'translateY(10px) scale(0.95)',
                    },
                    '100%': { opacity: 1, transform: 'translateY(0) scale(1)' },
                  },
                  '&:hover': {
                    background: '#263548',
                    borderColor: accentColor,
                  },
                }}
              >
                {/* Close button */}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowNudgePopup(false);
                  }}
                  sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    width: 18,
                    height: 18,
                    color: 'rgba(255,255,255,0.4)',
                    '&:hover': {
                      color: '#ef4444',
                      background: 'rgba(239,68,68,0.1)',
                    },
                  }}
                >
                  <CancelOutlined sx={{ fontSize: 12 }} />
                </IconButton>

                <Typography
                  sx={{
                    fontSize: '0.78rem',
                    fontWeight: 500,
                    color: '#f1f5f9',
                    pr: 2,
                    lineHeight: 1.4,
                  }}
                >
                  {t('need_some_help_i_apos_m_here')}
                </Typography>
              </Box>
            )}

            {/* FAB with badge */}
            <Badge
              variant="dot"
              invisible={!showBadge}
              sx={{
                '& .MuiBadge-dot': {
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: accentColor,
                  border: '2px solid #0f172a',
                  top: 4,
                  right: 4,
                  animation: showBadge
                    ? 'badge-pop 0.3s cubic-bezier(0.34,1.56,0.64,1) forwards'
                    : 'none',
                  '@keyframes badge-pop': {
                    '0%': { transform: 'scale(0)' },
                    '100%': { transform: 'scale(1)' },
                  },
                },
              }}
            >
              <Fab
                onClick={handleOpenChatbot}
                sx={{
                  width: 72,
                  height: 72,
                  backgroundColor: isDark ? '#0f172a' : '#f0f4ff',
                  boxShadow: isDark
                    ? '0 4px 20px rgba(0,0,0,0.4)'
                    : '0 4px 20px rgba(0,0,0,0.15)',
                  border: `2px solid ${accentColor}`,
                  borderRadius: '18px 18px 4px 18px',
                  p: 0,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    backgroundColor: isDark ? '#1e293b' : '#dce8ff',
                    boxShadow: `0 6px 28px rgba(0,0,0,0.5)`,
                    transform: 'scale(1.08)',
                  },
                  '&.MuiFab-root': {
                    backgroundColor: isDark ? '#0f172a' : '#f0f4ff',
                  },
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 1,
                  }}
                >
                  {clientLogo.type === 'img' ? (
                    <img
                      src={clientLogo.src}
                      alt={clientLogo.alt}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        filter: 'brightness(0) invert(1)',
                      }}
                    />
                  ) : (
                    <DarkModeLogo style={{ width: '100%', height: '100%' }} />
                  )}
                </Box>
              </Fab>
            </Badge>
          </Box>

          <ChatbotFloatingContainer isOpen={isOpen} setIsOpen={setIsOpen} />
        </Box>
      </ChatbotProvider>
    </CustomMuiThemeProvider>
  );
};
